
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Supplier } from '@/types/database.types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, MapPin } from 'lucide-react';

interface SupplierListProps {
  suppliers: Supplier[];
}

const SupplierList: React.FC<SupplierListProps> = ({ suppliers }) => {
  if (!suppliers.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8 gap-2">
          <p className="text-muted-foreground">No suppliers found</p>
          <Button asChild variant="outline">
            <Link to="/suppliers/new">Add Supplier</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Categories</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier.id}>
                <TableCell className="font-medium">{supplier.name}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{supplier.phone_number}</span>
                    </div>
                    {supplier.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{supplier.email}</span>
                      </div>
                    )}
                    {supplier.address && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{supplier.address}</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {supplier.region && <span>{supplier.region}</span>}
                    {supplier.city && <span className="text-sm text-muted-foreground">{supplier.city}</span>}
                    {!supplier.region && !supplier.city && <span className="text-xs text-muted-foreground">No location data</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {supplier.supplier_products && supplier.supplier_products.map(product => (
                      <Badge key={product.id} variant="outline" className="text-xs">
                        {product.product_name}
                      </Badge>
                    ))}
                    {(!supplier.supplier_products || supplier.supplier_products.length === 0) && (
                      <span className="text-xs text-muted-foreground">No products</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {supplier.supplier_categories && supplier.supplier_categories.map(category => (
                      <Badge key={category.id} variant="secondary" className="text-xs">
                        {category.category_name}
                      </Badge>
                    ))}
                    {(!supplier.supplier_categories || supplier.supplier_categories.length === 0) && (
                      <span className="text-xs text-muted-foreground">No categories</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={supplier.status === 'active' ? 'success' : 'secondary'}
                    className="capitalize"
                  >
                    {supplier.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/suppliers/${supplier.id.toString()}`}>View</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default SupplierList;
